import { createContext, useState, useContext, useEffect } from "react"
import { useAuth } from "./AuthProvider"
import { doc, getDoc, onSnapshot } from "firebase/firestore"
import { db } from "../services/firebase"

const UserContext = createContext(null)

export const useUser = () => {
  const context = useContext(UserContext)
  if (!context) {
    throw new Error("useUser must be used within a UserProvider")
  }
  return context
}

export const UserProvider = ({ children }) => {
  const { user } = useAuth()
  const [userData, setUserData] = useState(null)
  const [organizationData, setOrganizationData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Live listener on the user doc — picks up calendar connections and other changes instantly
  useEffect(() => {
    if (!user?.uid) {
      setUserData(null)
      setOrganizationData(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    const unsub = onSnapshot(
      doc(db, "users", user.uid),
      (snap) => {
        setUserData(snap.exists() ? snap.data() : null)
        setLoading(false)
      },
      (err) => {
        console.error("Error listening to user data:", err)
        setError("Failed to load user data")
        setLoading(false)
      },
    )
    return () => unsub()
  }, [user?.uid])

  // Fetch org whenever the user's organizationId changes
  useEffect(() => {
    const orgId = userData?.organizationId
    if (!orgId) { setOrganizationData(null); return }
    getDoc(doc(db, "organizations", orgId))
      .then((snap) => setOrganizationData(snap.exists() ? snap.data() : null))
      .catch((err) => { console.error("Error fetching organization data:", err); setOrganizationData(null) })
  }, [userData?.organizationId])

  const refreshUserData = async () => {
    const orgId = userData?.organizationId
    if (!orgId) return
    try {
      const snap = await getDoc(doc(db, "organizations", orgId))
      setOrganizationData(snap.exists() ? snap.data() : null)
    } catch (err) {
      console.error("Error refreshing organization data:", err)
    }
  }

  const isInOrganization = Boolean(userData?.organizationId)
  const isAdmin = userData?.organizationRole === "admin"
  const organizationId = userData?.organizationId || null
  const organizationName = organizationData?.name || userData?.organizationName || null
  const isInterviewer = userData?.accountType === "employer" && userData?.organizationRole === "interviewer"

  return (
    <UserContext.Provider
      value={{
        userData,
        loading,
        error,
        refreshUserData,
        organizationData,
        isInOrganization,
        isAdmin,
        organizationId,
        organizationName,
        isInterviewer,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}

export default UserProvider
